import React from 'react';
import { Space, Tag } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { renderCustomAttributeValue } from '@utils/customAttributeValue';
import { visibleCustomAttributeEntries } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';

interface InventoryBatchTagsProps {
  inventoryTypeName?: string | null;
  batch?: string | null;
  expirationDate?: string | Dayjs | null;
  manufacturingDate?: string | Dayjs | null;
  customAttributes?: Record<string, unknown> | null;
  // Green "available" tag is rendered only when a quantity is supplied (precise-mode selection).
  availableQuantity?: number | null;
}

const formatDate = (value?: string | Dayjs | null): string => (
  dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : (value ?? '')
);

const InventoryBatchTags: React.FC<InventoryBatchTagsProps> = ({
  inventoryTypeName,
  batch,
  expirationDate,
  manufacturingDate,
  customAttributes,
  availableQuantity,
}) => {
  const { t } = useTranslation();

  return (
    <Space size={4} wrap>
      {inventoryTypeName && <Tag color="blue" style={{ margin: 0 }}>{inventoryTypeName}</Tag>}
      {batch && <Tag style={{ margin: 0 }}>{batch}</Tag>}
      {expirationDate && <Tag style={{ margin: 0 }}>{formatDate(expirationDate)}</Tag>}
      {manufacturingDate && (
        <Tag style={{ margin: 0 }}>
          {t('models.inventory.manufacturing_date')}：{formatDate(manufacturingDate)}
        </Tag>
      )}
      {visibleCustomAttributeEntries(customAttributes).map(([key, value]) => (
        <Tag key={key} style={{ margin: 0 }}>{key}：{renderCustomAttributeValue(value)}</Tag>
      ))}
      {availableQuantity != null && (
        <Tag color="green" style={{ margin: 0 }}>
          {t('models.inventory.available_quantity', { defaultValue: '可用' })} {availableQuantity}
        </Tag>
      )}
    </Space>
  );
};

export default InventoryBatchTags;
